import { QueryClient, QueryCache, MutationCache } from "@tanstack/react-query";
import { isTrpcCauseShape } from "./trpc-errors.ts";

/**
 * Global handler for `subscription-ended` — any tRPC call to a non-allowlisted
 * procedure on a locked-out (`accessBlocked`) tenant lands here. We navigate
 * the browser to `/subscription-ended` (a paywall route outside the normal
 * workspace shell) so the user's only remaining action is to re-subscribe.
 *
 * Uses `window.location.assign` rather than the TanStack Router to (a) avoid
 * importing the router at module scope (circular init risk) and (b) blow away
 * any half-rendered workspace UI that would otherwise flash a stale layout for
 * a beat. @see ADR-0047.
 */
function handleSubscriptionEnded(err: unknown): void {
  if (!isTrpcCauseShape(err, { reason: "subscription-ended" })) return;
  // Guard against a loop while already on the paywall.
  if (window.location.pathname === "/subscription-ended") return;
  window.location.assign("/subscription-ended");
}

export function makeQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({ onError: handleSubscriptionEnded }),
    mutationCache: new MutationCache({ onError: handleSubscriptionEnded }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry: 1,
        refetchOnWindowFocus: false,
      },
      mutations: {
        retry: 0,
      },
    },
  });
}
